export function deepCompare<T>(a: T, b: T): boolean {
	return deepEqualInternal(a, b, new WeakMap())
}

function deepEqualInternal(a: unknown, b: unknown, seen: WeakMap<object, object>): boolean {
	// Fast path: reference equality (covers primitives and identical object refs)
	if (Object.is(a, b)) { return true }

	// Handle cases where one is null/undefined, or not objects
	if (a === null || b === null) { return false }
	const typeA = typeof a
	const typeB = typeof b
	if (typeA !== 'object' && typeB !== 'object') {
		// For primitives that weren't Object.is equal (e.g. -0 vs 0 already handled), they are not equal
		return false
	}
	if (typeA !== typeB) { return false }

	// From here both are non-null objects
	const objA = a as object
	const objB = b as object

	// Cycle detection: if we've seen objA, ensure it maps to the same objB
	const mapped = seen.get(objA)
	if (mapped) {
		return mapped === objB
	}
	seen.set(objA, objB)

	// Compare [[Class]] via toString tag
	const tagA = objectTag(objA)
	const tagB = objectTag(objB)
	if (tagA !== tagB) { return false }

	// Dates
	if (tagA === '[object Date]') {
		return (a as Date).getTime() === (b as Date).getTime()
	}

	// RegExp
	if (tagA === '[object RegExp]') {
		const ra = a as RegExp
		const rb = b as RegExp
		return ra.source === rb.source && ra.flags === rb.flags
	}

	// ArrayBuffer / DataView / TypedArrays: compare underlying bytes
	if (isArrayBufferView(a) && isArrayBufferView(b)) {
		const ua = toUint8ArrayView(a)
		const ub = toUint8ArrayView(b)
		return bytesEqual(ua, ub)
	}
	if (tagA === '[object ArrayBuffer]') {
		const ua = new Uint8Array(a as ArrayBuffer)
		const ub = new Uint8Array(b as ArrayBuffer)
		return bytesEqual(ua, ub)
	}

	// Map
	if (tagA === '[object Map]') {
		const ma = a as Map<unknown, unknown>
		const mb = b as Map<unknown, unknown>
		if (ma.size !== mb.size) { return false }
		// O(n^2) deep key/value matching to support structural key equality
		for (const [ka, va] of ma) {
			let found = false
			for (const [kb, vb] of mb) {
				if (deepEqualInternal(ka, kb, seen) && deepEqualInternal(va, vb, seen)) {
					found = true
					break
				}
			}
			if (!found) { return false }
		}
		return true
	}

	// Set
	if (tagA === '[object Set]') {
		const sa = a as Set<unknown>
		const sb = b as Set<unknown>
		if (sa.size !== sb.size) { return false }
		// O(n^2) deep membership check
		const used = new Set<number>()
		const arrB = Array.from(sb)
		outer:
		for (const va of sa) {
			for (let i = 0; i < arrB.length; i++) {
				if (used.has(i)) { continue }
				if (deepEqualInternal(va, arrB[i], seen)) {
					used.add(i)
					continue outer
				}
			}
			return false
		}
		return true
	}

	// Array
	if (Array.isArray(a)) {
		const aa = a as unknown[]
		const ab = b as unknown[]
		if (aa.length !== ab.length) { return false }
		for (let i = 0; i < aa.length; i++) {
			if (!deepEqualInternal(aa[i], ab[i], seen)) { return false }
		}
		return true
	}

	// Error
	if (tagA === '[object Error]') {
		const ea = a as Error
		const eb = b as Error
		return ea.name === eb.name && ea.message === eb.message
	}

	// Function - require identity equality (already failed Object.is), so false
	if (typeof a === 'function') { return false }

	// Different prototypes => not equal (ensures class instances match types)
	if (Object.getPrototypeOf(objA) !== Object.getPrototypeOf(objB)) { return false }

	// Plain object or class instance: compare own enumerable keys (string + symbol)
	const keysA = enumerableOwnKeys(objA)
	const keysB = enumerableOwnKeys(objB)
	if (keysA.length !== keysB.length) { return false }

	for (const key of keysA) {
		if (!Reflect.has(objB, key)) { return false }
		const valA = Reflect.get(objA as object, key)
		const valB = Reflect.get(objB as object, key)
		if (!deepEqualInternal(valA, valB, seen)) {
			return false
		}
	}

	return true
}

function objectTag(value: unknown): string {
	return Object.prototype.toString.call(value)
}

function isArrayBufferView(value: unknown): value is ArrayBufferView {
	// Includes TypedArrays and DataView
	return typeof value === 'object' && value !== null && ArrayBuffer.isView(value as ArrayBufferView)
}

function toUint8ArrayView(view: ArrayBufferView): Uint8Array {
	return new Uint8Array(view.buffer, view.byteOffset, view.byteLength)
}

function bytesEqual(a: Uint8Array, b: Uint8Array): boolean {
	if (a.byteLength !== b.byteLength) { return false }
	for (let i = 0; i < a.byteLength; i++) {
		if (a[i] !== b[i]) { return false }
	}
	return true
}

function enumerableOwnKeys(obj: object): Array<string | symbol> {
	const stringKeys = Object.keys(obj)
	const symbolKeys = Object.getOwnPropertySymbols(obj).filter((sym) =>
		Object.prototype.propertyIsEnumerable.call(obj, sym)
	)
	return [...stringKeys, ...symbolKeys]
}
